// ============================================================================
// LineMenu (L05) — menu « ... » d'une ligne d'employe dans la grille
// ============================================================================
// Port FIDELE du PoC components/salary/line-menu.tsx (116 L @ SHA 7e40e4a),
// consomme par EmployeeGrid. Popover manuel (pointerdown exterieur + Echap, meme
// pattern que ClassInfoButton) : deplacer vers une autre classe (pose le scenario
// a l'echelon 1 de la cible, ajustable ensuite par clic cellule), retirer du
// scenario, renommer (fictifs).
//
// TRANSPOSITION (PLAN-SOUS-LOTS §SL-C) : `Button` @/components/ui → boutons natifs
// dark-aware ; tokens shadcn → slate ; libelles FR en dur → cles i18n FR+EN (P13).
// Regle #4 : zero emoji ; Regle #28 : tokens dark-aware, zero CSS en dur couleur.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, MoreHorizontal, Pencil, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import type { EmployeeGridLine } from '../../lib/salary/positions';
import type { SalaryClass } from '../../lib/types/firestore';

const iconBtn =
  'inline-flex size-5 items-center justify-center rounded-md text-slate-400 transition-colors ' +
  'hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300 touch-manipulation';

export function LineMenu({
  line,
  classes,
  hasAnyScenario,
  onMoveToClass,
  onRemoveScenario,
  onRenameRequest,
}: {
  line: EmployeeGridLine;
  classes: SalaryClass[];
  // L'employe a un marqueur scenario quelque part (pas forcement cette ligne).
  hasAnyScenario: boolean;
  onMoveToClass?: (line: EmployeeGridLine, targetClassId: string) => void;
  onRemoveScenario?: (line: EmployeeGridLine) => void;
  onRenameRequest?: (line: EmployeeGridLine) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const otherClasses = classes.filter((c) => c.id !== line.class_id);

  function pick(action: () => void) {
    setOpen(false);
    action();
  }

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        className={iconBtn}
        aria-label={t('gest:planification.lineMenu.aria', { name: line.name })}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1 flex max-h-64 w-56 flex-col overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 text-left text-slate-800 shadow-md dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
          {onRenameRequest && line.virtual ? (
            <MenuItem onClick={() => pick(() => onRenameRequest(line))}>
              <Pencil size={14} aria-hidden="true" />
              {t('gest:planification.lineMenu.rename')}
            </MenuItem>
          ) : null}
          {onMoveToClass
            ? otherClasses.map((c) => (
                <MenuItem key={c.id} onClick={() => pick(() => onMoveToClass(line, c.id))}>
                  <ArrowRight size={14} aria-hidden="true" />
                  {t('gest:planification.lineMenu.moveTo', { class: c.label })}
                </MenuItem>
              ))
            : null}
          {onRemoveScenario && (hasAnyScenario || line.virtual) ? (
            <MenuItem onClick={() => pick(() => onRemoveScenario(line))}>
              <X size={14} aria-hidden="true" />
              {line.virtual
                ? t('gest:planification.lineMenu.removeVirtual')
                : t('gest:planification.lineMenu.removeScenario')}
            </MenuItem>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full touch-manipulation items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-field transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
    >
      {children}
    </button>
  );
}
